import React, { useRef, useEffect, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { usePopover } from '../../../contexts/PopoverProvider';
import {Link, useNavigate} from 'react-router-dom';
import styles from '../MainNav.module.css';

const NavPopover = ({ data, position, isMobile, type, id }) => {
    const [activeSection, setActiveSection] = useState(null);
    const popoverRef = useRef(null);
    const navigate = useNavigate();
    const { isPopoverOpen, closePopover } = usePopover();
    const getObjectSize = obj => Object.entries(obj).length;

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (popoverRef.current && !popoverRef.current.contains(event.target)) {
                closePopover();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [closePopover]);

    const handleClose = () => {
        closePopover();
    }

    const handleDocumentClick = (itemId, e) => {
        e.preventDefault();
        navigate(`/documents/${itemId}`);
        closePopover();
    };

    if (!isPopoverOpen(id)) return null;

    const renderContent = () => {
        if (type === 'assignedDocs') {
            // ... existing assignedDocs rendering logic ...
            return Object.entries(data || {}).map(([preparer, value]) => (
                <div key={preparer} className={styles.navPopoverSection}>
                    <button
                        onClick={() => setActiveSection(activeSection === preparer ? null : preparer)}
                        className={styles.navPopoverButton}
                    >
                        <span className={styles.navPopoverSpan}>{preparer}</span>
                        <div className="flex space-between items-center justify-center">
                            <span className={styles.documentBadge}>
                                {value.data?.length || 0}
                            </span>
                            {activeSection === preparer ? (
                                <ChevronDown className={styles.navPopoverIcon}/>
                            ) : (
                                 <ChevronRight className={styles.navPopoverIcon}/>
                             )}
                        </div>
                    </button>

                    {activeSection === preparer && value.data && (
                        <div className={styles.navPopoverContent}>
                            {value.data.map((item) => (
                                <div key={item.id} className={styles.navPopoverItem}>
                                    <a className={styles.navPopoverItemDoc}
                                       href={`/documents/${item.id}`}
                                       onClick={(e) => handleDocumentClick(item.id, e)}>
                                        {item.profile.client}
                                        <span className={styles.documentBadge}>
                                            {item.documents?.length || 0}
                                        </span>
                                    </a>
                                </div>
                            ))}
                            {value.data.length === 0 && (
                                <div className={styles.navPopoverEmpty}>No documents available</div>
                            )}
                        </div>
                    )}
                </div>
            ));
        } else if (type === 'newDocs') {
            return Object.values(data || {}).map((item) => (
                <div key={item.id} className={`${styles.navPopoverSection} ${activeSection === item.id ? styles.active : ''}`}>
                    <button
                        onClick={() => setActiveSection(activeSection === item.id ? null : item.id)}
                        className={`${styles.navPopoverButton}`}
                    >
                        <Link to={`/client-progress/${item.id}`} onClick={handleClose}>
                            <span className={styles.navPopoverSpan}>{item.profile.client}</span>
                        </Link>
                        <div className="flex space-between items-center justify-center">
                            <span className={styles.documentBadge}>
                                {item.documents?.length || 0}
                            </span>
                            {activeSection === item.id ? (
                                <ChevronDown className={styles.navPopoverIcon}/>
                            ) : (
                                 <ChevronRight className={styles.navPopoverIcon}/>
                             )}
                        </div>
                    </button>

                    {activeSection === item.id && (
                        <div className={styles.navPopoverContent}>
                            {item.documents?.map((doc) => (
                                <a
                                    key={doc.id}
                                    href={`/client-progress/${item.id}`}
                                    className={styles.navPopoverItemDoc}
                                    onClick={(e) => handleDocumentClick(item.id, e)}
                                >
                                    {doc.document_name || doc.document_comments}
                                </a>
                            ))}
                        </div>
                    )}
                </div>
            ));
        }
        return null;
    };

    let style = {};
    let dataLength = getObjectSize(data);
    if(isMobile) {
        style = {
            position: 'fixed',
            bottom: '66px',
            left: '0',
            right: '0',
            maxHeight: '80vh'
        }
    } else if(window.innerWidth < 1100){
        style = {
            position: 'fixed',
            top: dataLength >= 10 ? 'auto':'auto',
            bottom: dataLength >= 10 ? 'auto': 'auto',
            left: position.left,
            minWidth: '300px',
            height: dataLength >= 10 ? 'auto': 'auto',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: dataLength >= 10 ? 'start':'center',
            transform: "translate(-50%, -100%)"
        }
    } else {
        style = {
            position: 'fixed',
            top: dataLength >= 10 ? 0:'auto',
            bottom: dataLength >= 10 ? 0: 'auto',
            left: position.left,
            minWidth: '300px',
            maxHeight: dataLength >= 10 ? '100%':'auto',
            height: dataLength >= 10 ? window.innerHeight:'auto',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: dataLength >= 10 ? 'start':'center',
        }
    }


    return (
        <>
            <div
                ref={popoverRef}
                className={`${styles.navPopover} dark:shadow-white shadow-sm`}
                style={style}
            >
                {renderContent()}
            </div>
        </>
    );
};

const PopoverNavItem = ({ icon, label, data, active, onClick, type, count, dontPopover }) => {
    const [position, setPosition] = useState({ top: 0, left: 0 });
    const buttonRef = useRef(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const { openPopover, closePopover, isPopoverOpen } = usePopover();
    const popoverId = `${label}-popover`;

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleClick = (e) => {
        if (!isMobile) {
            const rect = buttonRef.current.getBoundingClientRect();
            setPosition({
                            top: rect.top,
                            left: rect.right + 8
                        });
        }

        if (isPopoverOpen(popoverId) || dontPopover) {
            closePopover();
        } else {
            openPopover(popoverId);
        }

        if (onClick) onClick();
    };

    return (
        <div className={styles.navItem} ref={buttonRef}>
            <div
                className={`${active ? styles.navItemActive : ''} ${styles.navItem}`}
                onClick={handleClick}
            >
                <div className={styles.navIcon}>
                    {React.cloneElement(icon, { size: 24 })}
                    {count > 0 && (
                        <span className={`${styles.documentBadge} ${styles.mobileAbsBadge}`}>
                        {count || 0}
                    </span>
                    )}
                </div>
                <span className={styles.navItemSpan}>{label}</span>
                {count > 0 && (
                    <span className={`${styles.documentBadge} ${styles.absBadge}`}>
                        {count || 0}
                    </span>
                )}
                {active && <div className={styles.navDot}/>}
            </div>
            {!dontPopover && (
                <NavPopover
                    id={popoverId}
                    data={data}
                    position={position}
                    isMobile={isMobile}
                    type={type}
                />
            )}

        </div>
    );
};

export default PopoverNavItem;
